import { useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import { AddCircleOutline, ExpandLess, ExpandMore, RemoveCircleOutline } from '@mui/icons-material';
import { Box, Card, Divider, IconButton, ListItem, Tooltip } from '@mui/material';
import { observer } from 'mobx-react-lite';
import { ConfirmModal, FuseInput, FuseSelect, SidebarItem, ToolbarContainer } from '/src/components';
import { useFilterStore } from '/src/context';
import { Lender } from '/src/lib/models';
import { FormErrors, TCreateParticipationRequest } from '/src/lib/types';
import { isNumber } from '/src/utils';

export type InvestorParticipationsProps = {
  /**
   * Whether this component is visible
   */
  open: boolean;
  /**
   * Toggles component visibility
   * @returns void
   */
  toggleOpen: () => void;
  /**
   * Participations to display
   */
  participations: TCreateParticipationRequest[];
  /**
   * Set Participations to display
   * @param participations Updated list of Participations
   * @returns void
   */
  setParticipations: (participations: TCreateParticipationRequest[]) => void;
  /**
   * Element flex amount
   */
  flex?: string;
};

export const InvestorParticipations: React.FC<InvestorParticipationsProps> = observer(
  ({ open, toggleOpen, participations, setParticipations, flex }) => {
    const rowHeight = 48;

    /** Context **/
    const filterStore = useFilterStore();

    /** State **/
    const [indexToDelete, setIndexToDelete] = useState<number>();
    const [formValues, setFormValues] = useState<TCreateParticipationRequest>({
      company: '',
      managementFee: 0,
      participationPercentage: 0,
    });
    const [formErrors, setFormErrors] = useState<FormErrors>({});

    /** Computed **/
    const totalParticipation = useMemo(() => {
      // Add up participation percentages
      let totalParticipation = 0;
      for (const participation of participations) {
        totalParticipation += participation.participationPercentage;
      }

      // Add any extra values if given
      totalParticipation += formValues.participationPercentage;

      // Return
      return totalParticipation;
    }, [participations, formValues.participationPercentage]);

    const lenders = useMemo(() => filterStore.lenderListGet() as Lender[], [filterStore._lenderList]);
    const lenderOptions = lenders?.map((lender) => ({ label: lender.name, value: lender._id }));

    /** Methods **/

    const updateFormValue = (key: string) => (value: any) => {
      if (formValues) {
        setFormValues({
          ...formValues,
          [key]: value,
        });
        // Clear error when field is updated
        if (formErrors[key]) {
          setFormErrors({
            ...formErrors,
            [key]: '',
          });
        }
      }
    };

    /**
     * Adds a participation interest
     */
    const addParticipation = () => {
      if (!formValues.company) {
        toast.error('Please select a Lender');
        return false;
      } else if (!formValues.participationPercentage) {
        toast.error('Please enter a Participation Percentage');
        return false;
      }

      const lenderParticipation: TCreateParticipationRequest = { ...formValues };

      // Push to lenders array

      const updatedParticipations = [...participations, lenderParticipation];
      setParticipations(updatedParticipations);

      setFormValues({
        company: '',
        managementFee: 0,
        participationPercentage: 0,
      });
    };

    /**
     * Removes a participation interest
     */
    const removeParticipation = () => {
      if (typeof indexToDelete !== 'number') return;

      participations.splice(indexToDelete, 1);
      setParticipations(participations);
    };

    /** Render **/
    return (
      <SidebarItem title="Investor Participations" open={open} toggleOpen={toggleOpen}>
        <Box display="flex" className="pad-sides" gap="6px">
          <Box display="flex" justifyContent="center" alignItems="center" width="48px">
            <IconButton className="md-icon-button" onClick={() => addParticipation()}>
              <Tooltip title="Add Participation">
                <AddCircleOutline className="md-icon material-icons" />
              </Tooltip>
            </IconButton>
          </Box>

          <Box display="flex" justifyContent="center" alignItems="end" flex={50}>
            <FuseSelect
              value={formValues.company}
              placeholder="Select Investor"
              ariaLabel="Select an Investor for this Participation"
              updateValue={updateFormValue('company')}
              required
              options={lenderOptions}
            />
          </Box>

          <Box display="flex" flex={40} gap="6px">
            <FuseInput
              label="Management Fee"
              value={formValues.managementFee}
              updateValue={(managementFee) =>
                updateFormValue('managementFee')(isNumber(managementFee) ? parseFloat(managementFee) : 0)
              }
              type="number"
              min="0"
              percentageMask
              boxProps={{ className: 'thin float-right' }}
              required
            />

            <FuseInput
              label="Participation %"
              value={formValues.participationPercentage}
              updateValue={(participationPercentage) =>
                updateFormValue('participationPercentage')(
                  isNumber(participationPercentage) ? parseFloat(participationPercentage) : 0
                )
              }
              type="number"
              min="0"
              percentageMask
              boxProps={{ className: 'thin float-right' }}
              required
            />
          </Box>
        </Box>

        <Divider />

        {participations.map((participation, i) => (
          <Box key={participation.company} display="flex" className="pad-sides" gap="6px">
            <Box display="flex" justifyContent="center" alignItems="center" width="48px">
              <IconButton
                className="md-icon-button"
                onClick={() => {
                  setIndexToDelete(i);
                }}
              >
                <Tooltip title="Remove Participation">
                  <RemoveCircleOutline />
                </Tooltip>
              </IconButton>
            </Box>

            <Box display="flex" justifyContent="center" alignItems="end" flex={50}>
              <FuseSelect
                id="participation.company"
                value={
                  typeof participation.company === 'string' ? participation.company : (participation.company as any)._id
                }
                placeholder="Select Investor"
                ariaLabel="Select an Investor for this Participation"
                updateValue={(company) => setParticipations(participations.with(i, { ...participations[i], company }))}
                options={lenderOptions}
              />
            </Box>

            <Box display="flex" flex={40} gap="6px">
              <Box flex={1}>
                <FuseInput
                  label="Management Fee"
                  value={participation.managementFee}
                  updateValue={(managementFee) =>
                    setParticipations(
                      participations.with(i, {
                        ...participations[i],
                        managementFee: isNumber(managementFee) ? parseFloat(managementFee) : 0,
                      })
                    )
                  }
                  type="number"
                  min="0"
                  percentageMask
                  required
                />
              </Box>

              <Box flex={1}>
                <FuseInput
                  label="Participation %"
                  value={participation.participationPercentage}
                  updateValue={(participationPercentage) => {
                    setParticipations(
                      participations.with(i, {
                        ...participations[i],
                        participationPercentage: isNumber(participationPercentage)
                          ? parseFloat(participationPercentage)
                          : 0,
                      })
                    );
                  }}
                  type="number"
                  min="0"
                  percentageMask
                  required
                />
              </Box>
            </Box>
          </Box>
        ))}

        <Divider />

        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          className="md-title pad-sides"
          height={rowHeight}
        >
          <small>
            <strong>Total</strong>
          </small>
          <Box color={totalParticipation === 1 ? 'green' : totalParticipation > 1 ? 'red' : '#000'}>
            {totalParticipation * 100}%
          </Box>
        </Box>
      </SidebarItem>
    );

    return (
      <Box display="flex" flexDirection="column" flex={flex}>
        <Card className="no-margin-bottom">
          <ToolbarContainer>
            <Box display="flex">Investor Participations</Box>
            <Box display="flex">
              <Box className="float-right">
                <IconButton
                  aria-label="Show/Hide Participations"
                  className="md-icon-button"
                  onClick={() => toggleOpen()}
                >
                  {open ? <ExpandLess /> : <ExpandMore />}
                </IconButton>
              </Box>
            </Box>
          </ToolbarContainer>
        </Card>
        {open && (
          <Card className="collapse">
            <ListItem sx={{ flexDirection: 'row' }}>
              <Tooltip title="Add Participation">
                <IconButton className="md-icon-button" onClick={() => addParticipation()}>
                  <AddCircleOutline />
                </IconButton>
              </Tooltip>

              <FuseSelect
                value={formValues.company}
                placeholder="Select Investor"
                ariaLabel="Select an Investor for this Participation"
                updateValue={updateFormValue('company')}
                required
                options={lenderOptions}
              />

              <FuseInput
                flex="20"
                label="Management Fee"
                // field="managementFee"
                // formValues={formValues}
                value={formValues.managementFee}
                updateValue={(managementFee) =>
                  updateFormValue('managementFee')(isNumber(managementFee) ? parseFloat(managementFee) : 0)
                }
                type="number"
                min="0"
                percentageMask
                boxProps={{ className: 'thin float-right' }}
                required
              />

              <FuseInput
                flex="20"
                label="Participation %"
                // field="participationPercentage"
                // formValues={formValues}
                value={formValues.participationPercentage}
                updateValue={(participationPercentage) =>
                  updateFormValue('participationPercentage')(
                    isNumber(participationPercentage) ? parseFloat(participationPercentage) : 0
                  )
                }
                type="number"
                min="0"
                percentageMask
                boxProps={{ className: 'thin float-right' }}
                required
              />
            </ListItem>

            <Divider />

            {participations.map((participation, i) => (
              <ListItem key={participation.company} sx={{ flexDirection: 'row' }}>
                <Tooltip title="Remove Participation">
                  <IconButton
                    className="md-icon-button"
                    onClick={() => {
                      setIndexToDelete(i);
                    }}
                  >
                    <RemoveCircleOutline />
                  </IconButton>
                </Tooltip>

                <FuseSelect
                  id="participation.company"
                  value={participation.company}
                  placeholder="Select Investor"
                  ariaLabel="Select an Investor for this Participation"
                  updateValue={(company) => setParticipations(participations.with(i, company))}
                  options={lenderOptions}
                />

                <FuseInput
                  flex="20"
                  label="Management Fee"
                  value={participation.managementFee}
                  updateValue={(managementFee) =>
                    setParticipations(
                      participations.with(i, {
                        ...participations[i],
                        managementFee: isNumber(managementFee) ? parseFloat(managementFee) : 0,
                      })
                    )
                  }
                  type="number"
                  min="0"
                  percentageMask
                  boxProps={{ className: 'thin float-right' }}
                  required
                />

                <FuseInput
                  flex="20"
                  label="Participation %"
                  value={participation.participationPercentage}
                  updateValue={(participationPercentage) => {
                    setParticipations(
                      participations.with(i, {
                        ...participations[i],
                        participationPercentage: isNumber(participationPercentage)
                          ? parseFloat(participationPercentage)
                          : 0,
                      })
                    );
                  }}
                  type="number"
                  min="0"
                  percentageMask
                  boxProps={{ className: 'thin float-right' }}
                  required
                />
              </ListItem>
            ))}

            <Divider />

            <ListItem className="md-title">
              <Box display="flex">
                <small>
                  <strong>Total</strong>
                </small>
              </Box>
              <Box display="flex">
                <Box
                  className="float-right"
                  sx={{
                    color: totalParticipation === 1 ? 'green' : totalParticipation > 1 ? 'red' : '#000',
                  }}
                >
                  {totalParticipation * 100}%
                </Box>
              </Box>
            </ListItem>
          </Card>
        )}

        <ConfirmModal
          content="Are you sure you want to remove this Participation?"
          label="Confirm Participation Removal"
          open={typeof indexToDelete === 'number'}
          setOpen={() => setIndexToDelete(undefined)}
          okLabel="Remove"
          onOk={() => removeParticipation()}
          cancelLabel="Cancel"
        />
      </Box>
    );
  }
);
